import { Box, Typography } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useProfile } from "../../../context/ProfileContext";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useDebounce } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { ISinisterList, SinisterService } from "../../../shared/services/api/sinister/SinisterService";
import { useClientOptions } from "../hooks/useClientOptions";
import { useHandleSearch } from "../hooks/useHandleSearch";
import { useManagerOptions } from "../hooks/useManagerOptions";
import { useSinisterFilters } from "../hooks/useSinisterFilters";
import { TableSinister } from "./TableSinister";
import { ToolbarSinisterGeneric } from "./ToolbarSinisterGeneric/ToolbarSinisterGeneric";
import { fieldsConfig } from "./ToolbarSinisterGeneric/fieldsConfig";

export const SinisterListPresentation = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { debounce } = useDebounce();

	const filtersFromSearchParams = useSinisterFilters(searchParams);
	const pagina = Number(searchParams.get("pagina") || "1");
	const limit = filtersFromSearchParams.limit;

	const [clientSearch, setClientSearch] = useState("");
	const [managerSearch, setManagerSearch] = useState("");
	const [rows, setRows] = useState<ISinisterList[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCount, setTotalCount] = useState(0);
	const [subStatusAccord, setSubStatusAccord] = useState<string>("Todos");
	const [shouldFetch, setShouldFetch] = useState(true);
	const { isSuperAdmin } = useProfile();
	const hasViewPermission = useMemo(() => {
		return isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.SinisterOptions, PermissionActionsEnum.View);
	}, [isSuperAdmin]);

	// Sincroniza os valores iniciais do autocomplete com o que veio na URL
	useEffect(() => {
		setClientSearch(filtersFromSearchParams.nameClient || "");
		setManagerSearch(filtersFromSearchParams.nameManager || "");
	}, [filtersFromSearchParams.nameClient, filtersFromSearchParams.nameManager]);

	const { clientOptions, loading: clientLoading } = useClientOptions(clientSearch);
	const { managerOptions, loading: managerLoading } = useManagerOptions(managerSearch);

	const handleSearch = useHandleSearch(setSearchParams, setShouldFetch);

	useEffect(() => {
		if (!shouldFetch) return;

		setIsLoading(true);
		debounce(() => {
			SinisterService.getAll(
				pagina,
				limit,
				filtersFromSearchParams.textSearch,
				filtersFromSearchParams.createdFrom,
				filtersFromSearchParams.statusSinister.join(","),
				filtersFromSearchParams.typePeriod,
				filtersFromSearchParams.busyImobile,
				filtersFromSearchParams.filterField,
				filtersFromSearchParams.selectedIdClient,
				filtersFromSearchParams.dataIni,
				filtersFromSearchParams.dataFim,
				filtersFromSearchParams.selectedIdManager,
				filtersFromSearchParams.isLMI.toString(),
				filtersFromSearchParams.isSketch.toString(),
				subStatusAccord
			).then((result) => {
				if (result instanceof Error) {
					alert(result.message);
				} else {
					setTotalCount(result.totalCount);
					setRows(result.data);
				}
				setIsLoading(false);
				setShouldFetch(false);
			});
		});
	}, [shouldFetch]);

	const dynamicFieldsConfig = useMemo(() => {
		return fieldsConfig.map((field) => {
			if (field.key === "selectedIdManager") {
				return {
					...field,
					extraProps: {
						...(field.extraProps || {}),
						options: managerOptions,
						loading: managerLoading,
						onInputChange: (_: any, val: string) => setManagerSearch(val),
					},
				};
			}
			if (field.key === "selectedIdClient") {
				return {
					...field,
					extraProps: {
						...(field.extraProps || {}),
						options: clientOptions,
						loading: clientLoading,
						onInputChange: (_: any, val: string) => setClientSearch(val),
					},
				};
			}
			return field;
		});
	}, [managerOptions, managerLoading, clientOptions, clientLoading]);

	function getFiltersDescription(filters: typeof filtersFromSearchParams): string {
		const cliente = filters.nameClient?.trim() || "todos os clientes";
		const statusOptions = fieldsConfig.find((f) => f.key === "statusSinister")?.options ?? [];
		const translatedStatus = (filters.statusSinister || []).map((s) => statusOptions.find((opt) => opt.value === s)?.label || s).join(", ");
		const dataIniFormat = isValid(parseISO(filters.dataIni)) ? format(parseISO(filters.dataIni), "dd/MM/yyyy") : "";
		const dataFimFormat = isValid(parseISO(filters.dataFim)) ? format(parseISO(filters.dataFim), "dd/MM/yyyy") : "";
		const periodo = dataIniFormat && dataFimFormat ? `no período de ${dataIniFormat} a ${dataFimFormat}` : "";
		const statusImovel =
			filters.busyImobile === "ocupado" ? "com imóvel ocupado" : filters.busyImobile === "desocupado" ? "com imóvel desocupado" : "em todos os imóveis";
		const saldoLmi = filters.isLMI === 1 ? "com saldo LMI esgotado" : "com saldo LMI";
		return `Exibindo sinistros de ${cliente} ${periodo}, ${statusImovel}, ${saldoLmi}, com status: ${translatedStatus}.`;
	}

	return (
		<>
			<LayoutBaseDePagina
				titulo="Sinistros"
				subtitulo="Listagem de sinistros"
				barraDeFerramentas={
					<>
						<ToolbarSinisterGeneric
							fieldsConfig={dynamicFieldsConfig}
							filterInitialParams={filtersFromSearchParams}
							onSearch={handleSearch}
							onClear={() => {
								setSearchParams({ status: filtersFromSearchParams.statusSinister.join(",") }, { replace: true });
								setShouldFetch(true);
							}}
						/>
						<Box px={2} mt={1}>
							<Typography variant="body2" color="text.secondary">
								{getFiltersDescription(filtersFromSearchParams)}
							</Typography>
						</Box>
					</>
				}
			>
				{hasViewPermission && (
					<TableSinister
						rows={rows}
						totalCount={totalCount}
						page={pagina - 1}
						rowsPerPage={limit}
						onPageChange={(newPage) => {
							setSearchParams(
								(prev) => {
									const params = new URLSearchParams(prev);
									params.set("pagina", String(newPage + 1));
									return params;
								},
								{ replace: true }
							);
							setShouldFetch(true);
						}}
						onRowsPerPageChange={(newLimit) => {
							setSearchParams(
								(prev) => {
									const params = new URLSearchParams(prev);
									params.set("limit", String(newLimit));
									params.set("pagina", "1");
									return params;
								},
								{ replace: true }
							);
							setShouldFetch(true);
						}}
					/>
				)}
			</LayoutBaseDePagina>
			<LoadingComp isopen={isLoading} />
		</>
	);
};
